import { createSelector } from 'reselect';

import { StateType } from '../../../../states';
import { type Selector } from '../../../../types/selectors';
import { selectActiveMissions, selectMissionsArray } from '../../selectors/missions';
import { MissionOperationTypes } from '../../types/state/missions';
import { type MissionState } from '../../types/state/missions';
import { selectMissionComplexeStatusComputationProps } from '../MissionCard/MissionCard.selector';

const getMissionPriorityLevelValue = ({ missionId, operationType }: MissionState, state: StateType): number => {
  let priorityLevel = 0;

  const { endDate, nbAchievementsPendingBets } = selectMissionComplexeStatusComputationProps(state, {
    missionId,
  });

  const hasMissionExpired = Date.now() > (endDate ?? Infinity);

  if (hasMissionExpired) {
    if (nbAchievementsPendingBets) {
      priorityLevel += 7500;
    } else {
      priorityLevel += 5000;
    }
  } else {
    priorityLevel += 10000;
  }

  if (operationType === MissionOperationTypes.MISSION) {
    priorityLevel += 100;
  }

  if (operationType === MissionOperationTypes.OTHER) {
    priorityLevel += 50;
  }

  return priorityLevel;
};

const selectOrderedMissionIds: Selector<Array<number>> = createSelector(
  [(state) => state, selectMissionsArray],
  (state, missions) =>
    missions
      .filter(
        (mission) =>
          Date.now() >= (mission.startDate ?? Infinity) &&
          !mission.isArchived &&
          ((mission.operationType === MissionOperationTypes.MISSION && mission.achievementIds.length) ||
            mission.operationType !== MissionOperationTypes.MISSION),
      )
      .sort((a, b) => b.startDate - a.startDate)
      .sort((a, b) => {
        const missionAPriorityLevel = getMissionPriorityLevelValue(a, state);
        const missionBPriorityLevel = getMissionPriorityLevelValue(b, state);

        return missionBPriorityLevel - missionAPriorityLevel;
      })
      .map((mission) => mission.missionId),
);

const selectMissionsCardsListContainerProps = createSelector(
  [selectOrderedMissionIds, selectActiveMissions],
  (missionIds, activeMissionIds) => ({
    missionIds,
    activeMissionIds,
  }),
);

export default selectMissionsCardsListContainerProps;
